import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useController, useForm, useWatch } from "react-hook-form";
import {
  getImageByIdApiImagesImageIdGetOptions,
  getImageByIdApiImagesImageIdGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import type { HydratedImage, ParameterGroup } from "../generated/api/types.gen";
import { MAX_REFERENCE_IMAGES } from "../lib/image-constants";
import { shouldAutoExtractParameters } from "../lib/parameter-panel-helpers";
import { navigateRedo, navigateUndo } from "../lib/refine-navigation";
import {
  type AddAssetPromptState,
  useAddAssetPrompt,
} from "./useAddAssetPrompt";
import { useImageIntake } from "./useImageIntake";
import { useParameterResyncPending } from "./useParameterResyncPending";
import { useRefineSelection } from "./useRefineSelection";
import { useRegeneration } from "./useRegeneration";

type RefineFormValues = {
  parameter_groups: ParameterGroup[];
  prompt: string;
  referenceImages: string[];
};

function useRefineForm(image: HydratedImage | undefined): {
  formMethods: UseFormReturn<RefineFormValues>;
  parameter_groups: ParameterGroup[];
  referenceImages: string[];
  setReferenceImages: (images: string[]) => void;
  addReferenceImage: (dataUrl: string) => boolean;
  isParametersDirty: boolean;
} {
  const formMethods = useForm<RefineFormValues>({
    defaultValues: { parameter_groups: [], prompt: "", referenceImages: [] },
    mode: "onChange",
  });
  const { control, reset, getValues } = formMethods;
  const parameter_groups = useWatch({ control, name: "parameter_groups" });
  const {
    field: { value: referenceImages, onChange: setReferenceImages },
  } = useController({ name: "referenceImages", control });
  // Appends to the form's current value rather than this render's: attached
  // images land asynchronously, and two landing before a re-render would
  // otherwise both extend the same stale list and one would be lost.
  const addReferenceImage = useCallback(
    (dataUrl: string) => {
      const current = getValues("referenceImages");
      if (current.length >= MAX_REFERENCE_IMAGES) return false;
      setReferenceImages([...current, dataUrl]);
      return true;
    },
    [getValues, setReferenceImages],
  );
  const {
    fieldState: { isDirty: isParametersDirty },
  } = useController({ name: "parameter_groups", control });
  const previousImageIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!image?.id) return;

    const imageChanged =
      previousImageIdRef.current !== undefined &&
      previousImageIdRef.current !== image.id;
    previousImageIdRef.current = image.id;

    if (imageChanged) {
      // Reference images are scoped to a single refined image and should not
      // carry across undo/redo navigation to a different image.
      reset({
        parameter_groups: image.parameter_groups ?? [],
        prompt: "",
        referenceImages: [],
      });
      return;
    }

    if (image.parameter_groups) {
      reset(
        { ...getValues(), parameter_groups: image.parameter_groups },
        { keepDirty: true },
      );
    }
  }, [image?.id, image?.parameter_groups, getValues, reset]);
  return {
    formMethods,
    parameter_groups,
    referenceImages,
    setReferenceImages,
    addReferenceImage,
    isParametersDirty,
  };
}

/** Collapsed prompt bar state. Collapsing clears the prompt so a hidden dirty
 *  value can't silently drive the next refinement. */
function usePromptBar(formMethods: UseFormReturn<RefineFormValues>) {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const { resetField } = formMethods;

  const closePrompt = useCallback(() => {
    setIsPromptOpen(false);
    resetField("prompt");
  }, [resetField]);

  return {
    isPromptOpen,
    openPrompt: () => setIsPromptOpen(true),
    closePrompt,
  };
}

export type RefinePageState = ReturnType<typeof useRefinePage>;

/** RefinePageState past the loading/error guards, so components can rely on
 *  `image` being present. */
export type LoadedRefinePage = RefinePageState & {
  image: NonNullable<RefinePageState["image"]>;
};

/** Attaches picked reference images through the downscale, offering each
 *  accepted one to the asset library. `isAttachingImage` feeds the shared
 *  regenerate-disabled rule, so no refine entry point can submit while an
 *  image is still being prepared. */
function useReferenceAttachments(
  referenceImages: string[],
  addReferenceImage: (dataUrl: string) => boolean,
  assetPrompt: AddAssetPromptState,
) {
  const { intake, isPending } = useImageIntake(
    MAX_REFERENCE_IMAGES - referenceImages.length,
    (dataUrl, file) => {
      if (addReferenceImage(dataUrl)) assetPrompt.promptToSave(dataUrl, file);
    },
  );
  return { attachReferenceImage: intake, isAttachingImage: isPending };
}

export function useRefinePage(imageId: string) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: image,
    isLoading,
    error,
  } = useQuery({
    ...getImageByIdApiImagesImageIdGetOptions({
      path: { image_id: imageId },
    }),
    enabled: Boolean(imageId),
    select: (data) => data as HydratedImage,
  });

  const {
    formMethods,
    parameter_groups,
    referenceImages,
    setReferenceImages,
    addReferenceImage,
    isParametersDirty,
  } = useRefineForm(image);
  const [isExtractingParameters, setIsExtractingParameters] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false);
  const promptBar = usePromptBar(formMethods);
  const isResyncingParameters = useParameterResyncPending(imageId);
  const assetPrompt = useAddAssetPrompt(image?.project_id);
  const attachments = useReferenceAttachments(
    referenceImages,
    addReferenceImage,
    assetPrompt,
  );
  const selection = useRefineSelection(imageId, image?.url);
  const shouldAutoExtract = shouldAutoExtractParameters({
    originalParameterGroups: image?.parameter_groups,
    isInitialLoading: isLoading,
    isExtractingParameters,
  });

  const regeneration = useRegeneration({
    refinementImage: image || null,
    onImageUpdate: (newImage) =>
      queryClient.setQueryData(
        getImageByIdApiImagesImageIdGetQueryKey({
          path: { image_id: newImage.id },
        }),
        newImage,
      ),
    formMethods,
  });

  return {
    image,
    isLoading,
    error,
    formMethods,
    parameter_groups,
    referenceImages,
    setReferenceImages,
    ...attachments,
    isParametersDirty,
    isDrawerCollapsed,
    setIsDrawerCollapsed,
    ...promptBar,
    handleUndo: () => image && navigateUndo(image, queryClient, navigate),
    handleRedo: () => image && navigateRedo(image, navigate),
    shouldAutoExtract,
    isExtractingParameters,
    setIsExtractingParameters,
    isResyncingParameters,
    assetPrompt,
    selection,
    ...regeneration,
    // A create-from-selection or replace-from-selection stream is a busy page
    // too: while it runs the user is about to be navigated, so every
    // refinement control locks.
    isRegenerating:
      regeneration.isRegenerating ||
      selection.isCreating ||
      selection.isReplacing,
  };
}
